import {
  Box, Button, Stack, Typography, TextField, InputAdornment,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Switch, Tooltip,
} from "@mui/material";
import FormatListBulletedAddIcon from "@mui/icons-material/FormatListBulletedAdd";
import DeleteIcon      from "@mui/icons-material/Delete";
import EditIcon        from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon       from "@mui/icons-material/Block";
import SpeedIcon       from "@mui/icons-material/Speed";
import RuleIcon        from "@mui/icons-material/Rule";
import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { startToast, updateToastError, updateToastSuccess, updateToastWarning, updateToastDismiss } from "../../../utils/notification-loading";
import API from "../../../api";
import WarningDialog from "../../../components/WarningDialog";

import {
  displayIpUi, translateIpUi, isIpUiAdvanced,
  IpBuilderDialog,
  displayPortUi, translatePortUi, parsePortString, isPortUiAdvanced,
  PortBuilderDialog,
} from "./IpPicker";

// ── Constants ─────────────────────────────────────────────────────────────────

const PROTOCOLS = [
  { value: "tcp",  label: "TCP",  color: "#449095" },
  { value: "udp",  label: "UDP",  color: "#7c5cbf" },
  { value: "icmp", label: "ICMP", color: "#2b7fc0" },
  { value: "dns",  label: "DNS",  color: "#c08a2b" },
];
const PROTOCOL_COLOR = Object.fromEntries(PROTOCOLS.map(p => [p.value, p.color]));

const CLASSIFICATIONS = [
  { value: "critical", label: "Critical", color: "#ce2528" },
  { value: "high",     label: "High",     color: "#ff8c00" },
  { value: "alert",    label: "Alert",    color: "#daa520" },
  { value: "movement", label: "Movement", color: "#58a8ae" },
];
const CLASSIFICATION_MAP = Object.fromEntries(CLASSIFICATIONS.map(c => [c.value, c]));

const DIALOG_META = {
  add:       { title: "Add Blacklist Rule",       Icon: RuleIcon,        btn: "Add Rule" },
  edit:      { title: "Edit Blacklist Rule",      Icon: EditIcon,        btn: "Save Changes" },
  duplicate: { title: "Duplicate Blacklist Rule", Icon: ContentCopyIcon, btn: "Duplicate" },
};

const ipRegex     = /^(ANY|((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/(3[0-2]|[12]?[0-9]))?)$/;
const domainRegex = /^(([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z]{2,}|(\*\.)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z]{2,})$/;

// Convert a legacy Suricata IP string from DB to a UI object
function ipStringToUi(ipStr) {
  if (!ipStr || ipStr.toUpperCase() === "ANY") return { type: "any" };
  const SURICATA = { "$HOME_NET": "home_net", "$EXTERNAL_NET": "external_net", "$DNS_SERVERS": "dns_servers", "$HTTP_SERVERS": "http_servers" };
  const preset = SURICATA[ipStr];
  if (preset) return { type: "preset", value: preset };
  return { type: "single", value: ipStr, negate: false };
}

function isIpFieldOk(ui, isDns) {
  if (!ui) return false;
  if (ui.type === "any" || ui.type === "preset" || ui.type === "list") return true;
  if (ui.type === "single") return !!ui.value && (ipRegex.test(ui.value) || (isDns && domainRegex.test(ui.value)));
  return false;
}
function isPortFieldOk(ui) {
  if (!ui) return false;
  if (ui.type === "any") return true;
  if (ui.type === "single") return /^\d+$/.test(ui.value ?? "") && Number(ui.value) >= 1 && Number(ui.value) <= 65535;
  if (ui.type === "range") return /^\d+$/.test(ui.from ?? "") && /^\d+$/.test(ui.to ?? "");
  if (ui.type === "list") return (ui.items?.length ?? 0) > 0;
  return false;
}
function checkPosInt(val, set, setErr) {
  const n = Number(val);
  setErr(val === "" || !Number.isInteger(n) || n < 1);
  set(val);
}

// ── Main component ────────────────────────────────────────────────────────────

const CustomRules = ({ rules, setRules }) => {
  const [filteredRules, setFilteredRules] = useState([]);
  const [search, setSearch]               = useState("");
  const [classFilter, setClassFilter]     = useState(null);
  const [open, setOpen]                   = useState(false);
  const [mode, setMode]                   = useState("add");
  const [editTarget, setEditTarget]       = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [openDelConfirm, setOpenDelConfirm] = useState(false);

  // Form fields — single source of truth is the _ui objects
  const [protocol, setProtocol]         = useState("tcp");
  const [src_ip_ui, setSrcIpUi]         = useState(null);
  const [dst_ip_ui, setDstIpUi]         = useState(null);
  const [dst_port_ui, setDstPortUi]     = useState(null);
  const [nameRule, setNameRule]         = useState("");
  const [nameManual, setNameManual]     = useState(false);
  const [responseEnabled, setResponse]  = useState(false);
  const [classification, setClassification] = useState("alert");
  const [threshEnabled, setThreshEnabled] = useState(false);
  const [threshCount, setThreshCount]   = useState("");
  const [threshSecs, setThreshSecs]     = useState("");
  const [tcntErr, setTcntErr]           = useState(false);
  const [tsecsErr, setTsecsErr]         = useState(false);

  // Picker dialog open states
  const [srcPickerOpen,  setSrcPickerOpen]  = useState(false);
  const [dstPickerOpen,  setDstPickerOpen]  = useState(false);
  const [portPickerOpen, setPortPickerOpen] = useState(false);

  const skipProtocolClear = useRef(false);
  const submittingRef     = useRef(false);
  const silentRef         = useRef(false);

  const noPort = protocol === "icmp";

  // Derived display values
  const srcIsAdvanced  = isIpUiAdvanced(src_ip_ui);
  const dstIsAdvanced  = isIpUiAdvanced(dst_ip_ui);
  const portIsAdvanced = !noPort && isPortUiAdvanced(dst_port_ui);

  const srcDisplayVal  = !src_ip_ui  ? "" : src_ip_ui.type  === "any" ? "any" : src_ip_ui.type  === "single" ? (src_ip_ui.value  ?? "") : "";
  const dstDisplayVal  = !dst_ip_ui  ? "" : dst_ip_ui.type  === "any" ? "any" : dst_ip_ui.type  === "single" ? (dst_ip_ui.value  ?? "") : "";
  const portDisplayVal = !dst_port_ui ? "" : dst_port_ui.type === "any" ? "any" : dst_port_ui.type === "single" ? (dst_port_ui.negate ? `!${dst_port_ui.value}` : (dst_port_ui.value ?? "")) : "";

  // Block submit when every field is "any" (matches all traffic)
  const allAny = src_ip_ui?.type === "any" && dst_ip_ui?.type === "any" && (noPort || dst_port_ui?.type === "any");

  // Response is only actionable for critical/high severity
  const responseAllowed = classification === "critical" || classification === "high";

  // Inline validation (only for single type)
  const srcSingleErr  = src_ip_ui?.type  === "single" && !!src_ip_ui.value  && !ipRegex.test(src_ip_ui.value);
  const dstSingleErr  = dst_ip_ui?.type  === "single" && !!dst_ip_ui.value  && (protocol === "dns" ? !ipRegex.test(dst_ip_ui.value) && !domainRegex.test(dst_ip_ui.value) : !ipRegex.test(dst_ip_ui.value));
  const portSingleErr = dst_port_ui?.type === "single" && !!dst_port_ui.value && !(/^\d+$/.test(dst_port_ui.value) && Number(dst_port_ui.value) >= 1 && Number(dst_port_ui.value) <= 65535);

  useEffect(() => {
    const base = classFilter ? rules.filter(r => r.classification === classFilter) : rules;
    if (!search) { setFilteredRules(base); return; }
    const re = new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
    setFilteredRules(base.filter(r => {
      const srcStr = r.src_ip_ui ? displayIpUi(r.src_ip_ui) : (r.src_ip ?? "");
      const dstStr = r.dst_ip_ui ? displayIpUi(r.dst_ip_ui) : (r.dst_ip ?? "");
      return re.test(srcStr) || re.test(dstStr) ||
        re.test(r.dst_port ?? "") || re.test(r.protocol ?? "") || re.test(r.ruleName ?? "");
    }));
  }, [search, classFilter, rules]);

  useEffect(() => {
    if (!open) return;
    if (skipProtocolClear.current) { skipProtocolClear.current = false; return; }
    setDstPortUi(null);
    setDstIpUi(null);
  }, [protocol]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || nameManual) return;
    const src = src_ip_ui ? displayIpUi(src_ip_ui) : "";
    const dst = dst_ip_ui ? displayIpUi(dst_ip_ui) : "";
    setNameRule(
      dst  ? `Blacklist ${src || "?"} -> ${dst}`
      : src ? `Blacklist ${src}`
      : "Blacklist Rule"
    );
  }, [src_ip_ui, dst_ip_ui, open]); // eslint-disable-line react-hooks/exhaustive-deps

  function reset() {
    setProtocol("tcp");
    setSrcIpUi(null); setDstIpUi(null); setDstPortUi(null);
    setNameRule(""); setNameManual(false);
    setResponse(false); setClassification("alert"); setThreshEnabled(false);
    setThreshCount(""); setThreshSecs("");
    setTcntErr(false); setTsecsErr(false);
    setSrcPickerOpen(false); setDstPickerOpen(false); setPortPickerOpen(false);
  }

  function prefillFromRule(rule) {
    skipProtocolClear.current = true;
    setProtocol(rule.protocol ?? "tcp");
    setSrcIpUi(rule.src_ip_ui ?? ipStringToUi(rule.src_ip));
    setDstIpUi(rule.dst_ip_ui ?? ipStringToUi(rule.dst_ip));
    setDstPortUi(parsePortString(rule.dst_port));
    setNameRule(rule.ruleName ?? "");
    setNameManual(true);
    setResponse(rule.response ?? false);
    setClassification(rule.classification ?? "alert");
    if (rule.threshold) {
      setThreshEnabled(true);
      const n = Number(rule.threshold.count);   setThreshCount(String(n)); setTcntErr(!Number.isInteger(n)  || n < 1);
      const s = Number(rule.threshold.seconds); setThreshSecs(String(s));  setTsecsErr(!Number.isInteger(s) || s < 1);
    } else {
      setThreshEnabled(false);
      setThreshCount(""); setThreshSecs(""); setTcntErr(false); setTsecsErr(false);
    }
  }

  function handleOpenAdd() {
    setMode("add"); setEditTarget(null); setOpen(true);
  }
  function handleOpenEdit(rule) {
    prefillFromRule(rule); setEditTarget(rule); setMode("edit"); setOpen(true);
  }
  function handleOpenDuplicate(rule) {
    prefillFromRule(rule); setEditTarget(null); setMode("duplicate"); setOpen(true);
  }
  function handleClose() {
    if (submittingRef.current) {
      updateToastDismiss();
      silentRef.current     = true;
      submittingRef.current = false;
    }
    setOpen(false); reset(); setMode("add"); setEditTarget(null);
  }

  function handleSubmit() {
    startToast();
    if (allAny) return updateToastWarning("Cannot create a rule that matches all traffic");

    const srcOk  = isIpFieldOk(src_ip_ui, false);
    const dstOk  = isIpFieldOk(dst_ip_ui, protocol === "dns");
    const portOk = noPort || isPortFieldOk(dst_port_ui);

    if (!srcOk || !dstOk || !portOk)  return updateToastWarning("Please fill in all required fields");
    if (!nameRule.trim())              return updateToastWarning("Rule name cannot be empty");
    if (threshEnabled && (tcntErr || tsecsErr || !threshCount || !threshSecs))
      return updateToastWarning("Please fill in the threshold fields");

    const threshold = threshEnabled
      ? { count: Number(threshCount), seconds: Number(threshSecs) }
      : null;

    const dst_port_str = noPort ? "ANY" : translatePortUi(dst_port_ui ?? { type: "any" });

    const payload = {
      protocol,
      src_ip_ui,
      dst_ip_ui,
      dst_port: dst_port_str,
      ruleName: nameRule.trim(),
      response: responseEnabled,
      classification,
      ...(threshold && { threshold }),
      action: "add",
    };

    const buildEntry = (sid, id) => ({
      protocol,
      src_ip_ui,
      dst_ip_ui,
      src_ip: translateIpUi(src_ip_ui),
      dst_ip: translateIpUi(dst_ip_ui),
      dst_port: dst_port_str,
      ruleName: nameRule.trim(),
      response: responseEnabled,
      classification,
      threshold,
      sid,
      id,
      timestamp: new Date(),
    });

    submittingRef.current = true;

    if (mode === "edit" && editTarget) {
      API.setBlacklist({ ...payload, sid: editTarget.sid, action: "edit" })
        .then(() => {
          submittingRef.current = false;
          const silent = silentRef.current; silentRef.current = false;
          setRules(prev => prev.map(r => r.sid === editTarget.sid ? buildEntry(editTarget.sid, editTarget.id) : r));
          if (!silent) { updateToastSuccess("Rule updated"); setOpen(false); reset(); setMode("add"); setEditTarget(null); }
        })
        .catch(() => {
          submittingRef.current = false;
          const silent = silentRef.current; silentRef.current = false;
          if (!silent) updateToastError("Error updating rule");
        });
    } else {
      API.setBlacklist(payload)
        .then((res) => {
          submittingRef.current = false;
          const silent = silentRef.current; silentRef.current = false;
          setRules(prev => [...prev, buildEntry(res.data.message, res.data.id)]);
          if (!silent) { updateToastSuccess(mode === "duplicate" ? "Rule duplicated" : "Rule added successfully"); setOpen(false); reset(); setMode("add"); setEditTarget(null); }
        })
        .catch((err) => {
          submittingRef.current = false;
          const silent = silentRef.current; silentRef.current = false;
          if (!silent) {
            if (err.status === 409) return updateToastWarning("Rule already exists");
            updateToastError("Error adding rule");
          }
        });
    }
  }

  function handleDelete(rule) {
    startToast();
    API.setBlacklist({ sid: rule?.sid, action: "del" })
      .then(() => { updateToastSuccess("Rule deleted"); setRules(prev => prev.filter(r => r.sid !== rule?.sid)); })
      .catch(() => updateToastError("Error deleting rule"));
  }

  const { title: dlgTitle, Icon: DlgIcon, btn: dlgBtn } = DIALOG_META[mode];

  return (
    <>
      {/* ── Panel header ──────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography sx={{ fontWeight: 600, color: "#e8e8e8" }}>Blacklist Rules</Typography>
          <Typography variant="caption" color="text.secondary">
            Define blacklist rules applied to the device's analysis engine.
          </Typography>
        </Box>
        <Button
          variant="contained" size="small" onClick={handleOpenAdd}
          startIcon={<FormatListBulletedAddIcon fontSize="small" />}
          sx={{ backgroundColor: "#449095", "&:hover": { backgroundColor: "#367a7e" }, flexShrink: 0, ml: 2 }}
        >
          Add Rule
        </Button>
      </Stack>

      {/* ── Search & classification filter ────────────────── */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2} flexWrap="wrap" rowGap={1}>
        <TextField
          size="small" label="Search rules" value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
        />
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          <Button size="small" onClick={() => setClassFilter(null)}
            sx={{ px: 1.75, py: 0.4, borderRadius: 4, fontSize: "0.7rem", fontWeight: 700,
              border: `1px solid ${!classFilter ? "#449095" : "#2a2a2a"}`,
              color: !classFilter ? "#449095" : "#555",
              backgroundColor: !classFilter ? "rgba(69,144,149,0.1)" : "transparent",
              "&:hover": { backgroundColor: "rgba(69,144,149,0.12)", borderColor: "#449095", color: "#449095" },
            }}>All</Button>
          {CLASSIFICATIONS.map(c => (
            <Button key={c.value} size="small"
              onClick={() => setClassFilter(prev => prev === c.value ? null : c.value)}
              sx={{ px: 1.75, py: 0.4, borderRadius: 4, fontSize: "0.7rem", fontWeight: 700,
                border: `1px solid ${classFilter === c.value ? c.color : "#2a2a2a"}`,
                color: classFilter === c.value ? c.color : "#555",
                backgroundColor: classFilter === c.value ? `${c.color}18` : "transparent",
                "&:hover": { backgroundColor: `${c.color}12`, borderColor: c.color, color: c.color },
                transition: "all 0.15s",
              }}>{c.label}</Button>
          ))}
        </Stack>
      </Stack>

      {/* ── Rule grid ─────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
        {filteredRules.length === 0 ? (
          <Typography sx={{ color: "#555", fontSize: "0.85rem", py: 4, textAlign: "center", gridColumn: "1 / -1" }}>
            {classFilter || search ? "No rules match the current filter" : "No blacklist rules defined"}
          </Typography>
        ) : filteredRules.map((rule, index) => (
          <Box key={rule.sid ?? index}
            sx={{ borderRadius: 1.5, border: "1px solid rgba(69,146,150,0.2)", backgroundColor: "rgba(69,146,150,0.06)", p: 1.75, display: "flex", flexDirection: "column", gap: 0.75 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
              <Chip
                label={rule.protocol?.toUpperCase()} size="small"
                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, backgroundColor: `${PROTOCOL_COLOR[rule.protocol] ?? "#449095"}22`, color: PROTOCOL_COLOR[rule.protocol] ?? "#449095", border: `1px solid ${PROTOCOL_COLOR[rule.protocol] ?? "#449095"}55` }}
              />
              {rule.classification && CLASSIFICATION_MAP[rule.classification] && (
                <Chip
                  label={CLASSIFICATION_MAP[rule.classification].label} size="small"
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700,
                    backgroundColor: `${CLASSIFICATION_MAP[rule.classification].color}22`,
                    color: CLASSIFICATION_MAP[rule.classification].color,
                    border: `1px solid ${CLASSIFICATION_MAP[rule.classification].color}55` }}
                />
              )}
              {!rule.response && (
                <Chip icon={<BlockIcon sx={{ fontSize: "0.7rem !important" }} />} label="no response" size="small"
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, backgroundColor: "rgba(192,57,43,0.12)", color: "#c0392b", border: "1px solid rgba(192,57,43,0.3)" }} />
              )}
              {rule.threshold && (
                <Chip icon={<SpeedIcon sx={{ fontSize: "0.7rem !important" }} />} label={`≥${rule.threshold.count} pkt / ${rule.threshold.seconds}s`} size="small"
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, backgroundColor: "rgba(150,100,200,0.12)", color: "#a07cd0", border: "1px solid rgba(150,100,200,0.3)" }} />
              )}
            </Stack>

            <Tooltip title={rule.ruleName ?? ""} placement="top" disableHoverListener={(rule.ruleName?.length ?? 0) < 28}>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#d0d0d0", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {rule.ruleName || <span style={{ color: "#555", fontStyle: "italic" }}>—</span>}
              </Typography>
            </Tooltip>

            <Typography sx={{ fontSize: "0.79rem", color: "#999" }}>
              <span style={{ color: "#ccc" }}>{rule.src_ip_ui ? displayIpUi(rule.src_ip_ui) : (rule.src_ip?.toUpperCase() === "ANY" ? "any" : (rule.src_ip ?? "—"))}</span>
              <ArrowForwardIcon sx={{ fontSize: 11, mx: 0.5, verticalAlign: "middle", color: "#444" }} />
              <span style={{ color: "#ccc" }}>{rule.dst_ip_ui ? displayIpUi(rule.dst_ip_ui) : (rule.dst_ip?.toUpperCase() === "ANY" ? "any" : (rule.dst_ip ?? "—"))}</span>
              {rule.protocol !== "icmp" && rule.dst_port && (
                <span style={{ color: "#449095" }}>{`:${rule.dst_port.toUpperCase() === "ANY" ? "any" : rule.dst_port}`}</span>
              )}
            </Typography>

            {rule.id && (
              <Tooltip title="Click to copy" placement="top">
                <Typography
                  onClick={() => { navigator.clipboard.writeText(rule.id); updateToastSuccess("Rule ID copied"); }}
                  sx={{ fontSize: "0.7rem", color: "#777", fontFamily: "monospace", lineHeight: 1.3,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer",
                    "&:hover": { color: "#449095" } }}
                >
                  ID: {rule.id}
                </Typography>
              </Tooltip>
            )}

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={{ fontSize: "0.64rem", color: "#444" }}>
                {dayjs(rule.timestamp).format("YYYY-MM-DD HH:mm")}
              </Typography>
              <Stack direction="row" spacing={0.25}>
                <Tooltip title="Edit" placement="top">
                  <IconButton size="small" onClick={() => handleOpenEdit(rule)}
                    sx={{ p: 0.5, color: "#449095", "&:hover": { backgroundColor: "rgba(69,144,149,0.12)" } }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplicate" placement="top">
                  <IconButton size="small" onClick={() => handleOpenDuplicate(rule)}
                    sx={{ p: 0.5, color: "#666", "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "#aaa" } }}>
                    <ContentCopyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" placement="top">
                  <IconButton size="small" onClick={() => { setPendingDelete(rule); setOpenDelConfirm(true); }}
                    sx={{ p: 0.5, color: "#c0392b", "&:hover": { backgroundColor: "rgba(192,57,43,0.1)" } }}>
                    <DeleteIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Box>

      {/* ── Delete confirmation ────────────────────────────── */}
      <WarningDialog
        open={openDelConfirm} setOpen={setOpenDelConfirm}
        setCheck={(val) => { setOpenDelConfirm(false); if (val && pendingDelete) { handleDelete(pendingDelete); setPendingDelete(null); } }}
        message={`The rule "${pendingDelete?.ruleName ?? ""}" will be permanently removed from the device.`}
      />

      {/* ── Rule form dialog ───────────────────────────────── */}
      <Dialog open={open} fullWidth maxWidth="lg" onClose={handleClose}
        PaperProps={{ sx: { backgroundColor: "#141414", border: "1px solid #252525", borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e1e1e", py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
            <DlgIcon sx={{ color: "#449095", fontSize: 22 }} />
            <Typography fontSize={17} fontWeight={700} letterSpacing="0.01em">{dlgTitle}</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 0, marginTop: 2, marginBottom: 2 }}>
          <Stack spacing={2}>
            {/* ── Traffic ──────────────────────────────────── */}
            <SectionCard label="TRAFFIC" accent="#449095" sx={{ mb: 0 }}>
              <Stack direction="row" spacing={1} mb={2.5}>
                {PROTOCOLS.map(p => (
                  <ProtocolPill key={p.value} proto={p} selected={protocol === p.value} onClick={() => setProtocol(p.value)} />
                ))}
              </Stack>
              <Stack spacing={0}>
                {/* Source */}
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Box sx={{ width: 36, flexShrink: 0, pt: 1.15, display: "flex", justifyContent: "flex-end" }}>
                    <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: "#449095", letterSpacing: "0.08em" }}>FROM</Typography>
                  </Box>
                  <IpField
                    label="Source IP"
                    placeholder="IP / CIDR"
                    isAdvanced={srcIsAdvanced}
                    displayVal={srcDisplayVal}
                    uiVal={src_ip_ui}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setSrcIpUi(val === "" ? null : val === "ANY" ? { type: "any" } : { type: "single", value: val, negate: false });
                    }}
                    onAny={() => setSrcIpUi({ type: "any" })}
                    onPickerOpen={() => setSrcPickerOpen(true)}
                    error={srcSingleErr}
                    helperText="Invalid IP / CIDR"
                  />
                </Stack>
                <Box sx={{ pl: "18px", mt: -0.5, mb: -0.5 }}>
                  <Box sx={{ width: "1px", height: 12, backgroundColor: "#2a2a2a" }} />
                </Box>
                {/* Destination */}
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Box sx={{ width: 36, flexShrink: 0, pt: 1.15, display: "flex", justifyContent: "flex-end" }}>
                    <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: "#449095", letterSpacing: "0.08em" }}>TO</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flex={1} alignItems="flex-start">
                    {protocol === "dns" ? (
                      <TextField
                        label="Domain / IP" size="small"
                        value={dstIsAdvanced ? displayIpUi(dst_ip_ui) : dstDisplayVal}
                        placeholder="e.g. sub.example.com or 1.2.3.4"
                        inputProps={dstIsAdvanced ? { readOnly: true, style: { color: "#449095", fontStyle: "italic", fontSize: "0.82rem" } } : {}}
                        onChange={dstIsAdvanced ? undefined : (e) => {
                          const val = e.target.value;
                          setDstIpUi(val === "" ? null : { type: "single", value: val, negate: false });
                        }}
                        error={!dstIsAdvanced && dstSingleErr}
                        helperText={!dstIsAdvanced && dstSingleErr ? "Invalid domain or IP" : " "}
                        sx={{ flex: 1, ...(dstIsAdvanced ? advancedFieldSx : {}) }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ gap: 0.5 }}>
                              <IconButton size="small" tabIndex={-1} onClick={() => setDstPickerOpen(true)}
                                sx={{ p: 0.35, color: dstIsAdvanced ? "#449095" : "#555", "&:hover": { color: "#449095", backgroundColor: "rgba(69,144,149,0.1)" } }}>
                                <EditIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    ) : (
                      <IpField
                        label="Destination IP"
                        placeholder="IP / CIDR"
                        isAdvanced={dstIsAdvanced}
                        displayVal={dstDisplayVal}
                        uiVal={dst_ip_ui}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase();
                          setDstIpUi(val === "" ? null : val === "ANY" ? { type: "any" } : { type: "single", value: val, negate: false });
                        }}
                        onAny={() => setDstIpUi({ type: "any" })}
                        onPickerOpen={() => setDstPickerOpen(true)}
                        error={dstSingleErr}
                        helperText="Invalid IP / CIDR"
                      />
                    )}
                    {!noPort && (
                      <>
                        <Typography sx={{ color: "#444", fontWeight: 700, fontSize: 18, pt: 1, flexShrink: 0 }}>:</Typography>
                        <TextField
                          label="Port" size="small"
                          value={portIsAdvanced ? displayPortUi(dst_port_ui) : portDisplayVal}
                          placeholder="80, 80:443, [80,443]"
                          inputProps={portIsAdvanced ? { readOnly: true, style: { color: "#449095", fontStyle: "italic", fontSize: "0.82rem" } } : {}}
                          onChange={portIsAdvanced ? undefined : (e) => {
                            const val = e.target.value;
                            setDstPortUi(val === "" ? null : val.toUpperCase() === "ANY" ? { type: "any" } : { type: "single", value: val, negate: false });
                          }}
                          error={!portIsAdvanced && portSingleErr}
                          helperText={!portIsAdvanced && portSingleErr ? "Invalid port" : " "}
                          sx={{ width: 200, ...(portIsAdvanced ? advancedFieldSx : {}) }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ gap: 0.5 }}>
                                {!portIsAdvanced && <AnyButton onClick={() => setDstPortUi({ type: "any" })} />}
                                <IconButton size="small" tabIndex={-1} onClick={() => setPortPickerOpen(true)}
                                  sx={{ p: 0.35, color: portIsAdvanced ? "#449095" : "#555", "&:hover": { color: "#449095", backgroundColor: "rgba(69,144,149,0.1)" } }}>
                                  <EditIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </>
                    )}
                  </Stack>
                </Stack>
              </Stack>
              {allAny && (
                <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.3)" }}>
                  <Typography sx={{ fontSize: "0.78rem", color: "#c0392b", fontWeight: 500 }}>
                    Source, destination and port are all set to "any" — this would match all traffic. Specify at least one field.
                  </Typography>
                </Box>
              )}
            </SectionCard>

            {/* ── Row 2: Rule Name | Threshold ─────────────── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <SectionCard label="RULE NAME" accent="#449095" sx={{ mb: 0 }}>
                <TextField
                  fullWidth size="small" value={nameRule} placeholder="Blacklist Rule"
                  onChange={(e) => { setNameRule(e.target.value); setNameManual(true); }}
                  inputProps={{ maxLength: 80 }}
                  helperText={(nameManual ? "Custom name" : "Auto-generated from IP fields — edit to override") + ". This will appear as the detail in the security logs"}
                />
              </SectionCard>
              <SectionCard label="THRESHOLD" accent="#449095" sx={{ mb: 0 }}>
                <ToggleRow
                  label="Enable packet threshold"
                  description={threshEnabled ? "Trigger only after seeing N matching packets in the time window" : "Trigger only after reaching a minimum number of packets"}
                  checked={threshEnabled} onChange={setThreshEnabled} accentColor="#449095"
                />
                {!threshEnabled && (
                  <Typography sx={{ fontSize: "0.72rem", color: "#666", mt: 0.75, fontStyle: "italic" }}>
                    Without a threshold the rule fires on the first matching packet.
                  </Typography>
                )}
                {threshEnabled && (
                  <Stack direction="row" alignItems="center" spacing={1} mt={1.25} flexWrap="wrap">
                    <Typography sx={{ fontSize: "0.75rem", color: "#555", fontStyle: "italic" }}>↳</Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>Trigger if</Typography>
                    <TextField size="small" type="number" value={threshCount}
                      onChange={(e) => checkPosInt(e.target.value, setThreshCount, setTcntErr)}
                      error={tcntErr} inputProps={{ min: 1 }}
                      sx={{ width: 72, "& .MuiInputBase-input": { py: 0.65, px: 1 } }}
                    />
                    <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>pkts in</Typography>
                    <TextField size="small" type="number" value={threshSecs}
                      onChange={(e) => checkPosInt(e.target.value, setThreshSecs, setTsecsErr)}
                      error={tsecsErr} inputProps={{ min: 1 }}
                      sx={{ width: 72, "& .MuiInputBase-input": { py: 0.65, px: 1 } }}
                    />
                    <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>s</Typography>
                  </Stack>
                )}
              </SectionCard>
            </Box>

            {/* ── Row 3: Classification | Response ─────────── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <SectionCard label="CLASSIFICATION" accent="#449095" sx={{ mb: 0 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {CLASSIFICATIONS.map(c => (
                    <Button key={c.value} size="small"
                      onClick={() => {
                        setClassification(c.value);
                        if (c.value !== "critical" && c.value !== "high") setResponse(false);
                      }}
                      sx={{
                        px: 2.5, py: 0.6, borderRadius: 4,
                        fontSize: "0.72rem", fontWeight: 700,
                        border: `1px solid ${classification === c.value ? c.color : "#2a2a2a"}`,
                        color: classification === c.value ? c.color : "#555",
                        backgroundColor: classification === c.value ? `${c.color}18` : "transparent",
                        "&:hover": { backgroundColor: `${c.color}12`, borderColor: c.color, color: c.color },
                        transition: "all 0.15s",
                      }}
                    >
                      {c.label}
                    </Button>
                  ))}
                </Stack>
              </SectionCard>
              <SectionCard label="RESPONSE" accent="#449095" sx={{ mb: 0 }}>
                <ToggleRow
                  label="Enable response action"
                  description={
                    !responseAllowed
                      ? 'Available only for "critical" and "high" classifications'
                      : responseEnabled
                        ? "Device will actively react when this rule triggers"
                        : "Rule will log only — no active countermeasure"
                  }
                  checked={responseEnabled} onChange={setResponse} accentColor="#449095"
                  disabled={!responseAllowed}
                />
              </SectionCard>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #1e1e1e", px: 3, py: 2, justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            {mode === "edit" ? "Editing replaces the existing rule on the device." : "Once triggered, logs at most once every 5 minutes."}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" onClick={handleClose}
              sx={{ minWidth: 90, color: "#888", borderColor: "rgba(255,255,255,0.12)", "&:hover": { borderColor: "#666", backgroundColor: "rgba(255,255,255,0.04)", color: "#bbb" } }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={allAny}
              sx={{ minWidth: 110, backgroundColor: "#449095", "&:hover": { backgroundColor: "#367a7e" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" } }}>
              {dlgBtn}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* ── IP picker dialogs ──────────────────────────────── */}
      <IpBuilderDialog
        open={srcPickerOpen}
        onClose={() => setSrcPickerOpen(false)}
        initialValue={src_ip_ui}
        onConfirm={(v) => { setSrcIpUi(v); setSrcPickerOpen(false); }}
      />
      <IpBuilderDialog
        open={dstPickerOpen}
        onClose={() => setDstPickerOpen(false)}
        initialValue={dst_ip_ui}
        onConfirm={(v) => { setDstIpUi(v); setDstPickerOpen(false); }}
        forDns={protocol === "dns"}
      />
      <PortBuilderDialog
        open={portPickerOpen}
        onClose={() => setPortPickerOpen(false)}
        initialValue={dst_port_ui}
        onConfirm={(v) => { setDstPortUi(v); setPortPickerOpen(false); }}
      />
    </>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const AnyButton = ({ onClick }) => (
  <Button size="small" tabIndex={-1} onClick={onClick}
    sx={{ minWidth: 0, px: 1, py: 0.1, fontSize: "0.6rem", fontWeight: 700, lineHeight: 1.8, color: "#449095", border: "1px solid rgba(69,144,149,0.35)", borderRadius: 0.75, "&:hover": { backgroundColor: "rgba(69,144,149,0.12)", borderColor: "#449095" } }}>
    any
  </Button>
);

const ProtocolPill = ({ proto, selected, onClick }) => (
  <Button size="small" onClick={onClick}
    sx={{ px: 2.5, py: 0.6, borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, border: `1px solid ${selected ? proto.color : "#2a2a2a"}`, color: selected ? proto.color : "#555", backgroundColor: selected ? `${proto.color}18` : "transparent", "&:hover": { backgroundColor: `${proto.color}12`, borderColor: proto.color, color: proto.color }, transition: "all 0.15s" }}>
    {proto.label}
  </Button>
);

const SectionCard = ({ label, accent, children, sx: sxProp = {} }) => (
  <Box sx={{ mb: 2, borderRadius: 1.5, border: "1px solid #222", backgroundColor: "#0f0f0f", overflow: "hidden", ...sxProp }}>
    <Box sx={{ px: 2, py: 0.75, backgroundColor: "#0a0a0a", borderBottom: "1px solid #1e1e1e", borderLeft: `3px solid ${accent}` }}>
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: accent, letterSpacing: "0.12em", opacity: 0.85 }}>{label}</Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const ToggleRow = ({ label, description, checked, onChange, accentColor, disabled = false }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ opacity: disabled ? 0.5 : 1 }}>
    <Box>
      <Typography sx={{ fontSize: "0.88rem", fontWeight: 500, color: !disabled && checked ? "#e0e0e0" : "#888" }}>{label}</Typography>
      <Typography variant="caption" sx={{ color: !disabled && checked ? "#aaa" : "#555" }}>{description}</Typography>
    </Box>
    <Switch checked={checked} disabled={disabled} size="small" onChange={(e) => onChange(e.target.checked)}
      sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: accentColor } }}
    />
  </Stack>
);

// ── Advanced field styling ────────────────────────────────────────────────────

const advancedFieldSx = {
  "& .MuiOutlinedInput-root fieldset": { borderColor: "rgba(69,144,149,0.45) !important" },
  "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "rgba(69,144,149,0.7) !important" },
};

// IpField renders a TextField that switches between simple (editable) and advanced (read-only
// teal display) modes depending on the isAdvanced flag. Must be module-level so React sees a
// stable component identity across renders.
const IpField = ({ label, placeholder, isAdvanced, displayVal, uiVal, onChange, onAny, onPickerOpen, error, helperText }) => (
  <TextField
    label={label} size="small"
    value={isAdvanced ? displayIpUi(uiVal) : displayVal}
    placeholder={placeholder}
    inputProps={isAdvanced ? { readOnly: true, style: { color: "#449095", fontStyle: "italic", fontSize: "0.82rem" } } : {}}
    onChange={isAdvanced ? undefined : onChange}
    error={!isAdvanced && error}
    helperText={!isAdvanced && error ? helperText : " "}
    sx={{ flex: 1, ...(isAdvanced ? advancedFieldSx : {}) }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end" sx={{ gap: 0.5 }}>
          {!isAdvanced && <AnyButton onClick={onAny} />}
          <IconButton size="small" tabIndex={-1} onClick={onPickerOpen}
            sx={{ p: 0.35, color: isAdvanced ? "#449095" : "#555", "&:hover": { color: "#449095", backgroundColor: "rgba(69,144,149,0.1)" } }}>
            <EditIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </InputAdornment>
      )
    }}
  />
);

export default CustomRules;
